import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  useCreateEvent,
  useEventLocations,
  useUpdateEvent,
  type Discipline,
  type EventResponse,
} from '@/hooks/useEvents'
import { ApiError } from '@/lib/api/rawFetch'

// Numeric fields are held as strings (native number inputs emit strings) and
// converted in onSubmit — same convention as AddVendorModal.
const numStr = (min: number, label: string) =>
  z.string().refine(
    (v) => v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= min,
    { message: `${label} must be ≥ ${min}` }
  )

const eventSchema = z.object({
  shopify_location_id: z.string().min(1, 'Location is required'),
  event_date: z.string().min(1, 'Date is required'),
  event_duration_days: numStr(1, 'Duration'),
  discipline: z.enum(['hunter_jumper', 'dressage', 'eventing']),
  booth_fee: numStr(0, 'Booth fee'),
  labor_cost: numStr(0, 'Labor cost'),
  travel_lodging: numStr(0, 'Travel & lodging'),
})

type EventFormValues = z.infer<typeof eventSchema>

const EMPTY: EventFormValues = {
  shopify_location_id: '',
  event_date: '',
  event_duration_days: '1',
  discipline: 'eventing',
  booth_fee: '0',
  labor_cost: '0',
  travel_lodging: '0',
}

export const DISCIPLINE_LABELS: Record<string, string> = {
  hunter_jumper: 'Hunter Jumper',
  dressage: 'Dressage',
  eventing: 'Eventing',
}

export function EventFormModal({
  open,
  onOpenChange,
  event,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When set, the modal edits this event; otherwise it creates a new one. */
  event?: EventResponse | null
}) {
  const { data: locations = [] } = useEventLocations()
  const create = useCreateEvent()
  const update = useUpdateEvent()
  const busy = create.isPending || update.isPending
  const editing = !!event

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: EMPTY,
  })

  React.useEffect(() => {
    if (open) {
      form.reset(
        event
          ? {
              shopify_location_id: String(event.shopify_location_id),
              event_date: event.event_date,
              event_duration_days: String(event.event_duration_days),
              discipline: (event.discipline as Discipline) ?? 'eventing',
              booth_fee: String(event.booth_fee),
              labor_cost: String(event.labor_cost),
              travel_lodging: String(event.travel_lodging),
            }
          : EMPTY
      )
      create.reset()
      update.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event])

  async function onSubmit(values: EventFormValues) {
    const body = {
      shopify_location_id: Number(values.shopify_location_id),
      event_date: values.event_date,
      event_duration_days: Math.trunc(Number(values.event_duration_days)),
      discipline: values.discipline,
      booth_fee: Number(values.booth_fee),
      labor_cost: Number(values.labor_cost),
      travel_lodging: Number(values.travel_lodging),
    }
    try {
      if (editing && event) {
        await update.mutateAsync({ eventId: event.id, body })
        toast.success('Event updated')
      } else {
        await create.mutateAsync(body)
        toast.success('Event week registered')
      }
      onOpenChange(false)
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 409
          ? 'An event at this location on this date already exists'
          : err instanceof Error
            ? err.message
            : 'Failed to save event'
      toast.error(msg)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit event week' : 'Register event week'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Update dates, discipline, or event costs.'
              : 'Registers one event week at a POS location. Costs can be filled in later.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="shopify_location_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>POS location</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={busy}
                      autoFocus={!editing}
                      data-testid="event-location"
                      className="h-9 w-full border border-[var(--line-input)] bg-transparent px-2 text-sm focus:outline-none focus:border-[var(--brand)]"
                    >
                      <option value="">— select location —</option>
                      {locations.map((l) => (
                        <option key={l.location_id} value={String(l.location_id)}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="event_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} disabled={busy} data-testid="event-date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="event_duration_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duration (d)</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={60} {...field} disabled={busy} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="discipline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Discipline</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        disabled={busy}
                        className="h-9 w-full border border-[var(--line-input)] bg-transparent px-2 text-sm focus:outline-none focus:border-[var(--brand)]"
                      >
                        {Object.entries(DISCIPLINE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="booth_fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Booth fee ($)</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" {...field} disabled={busy} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="labor_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Labor ($)</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" {...field} disabled={busy} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="travel_lodging"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Travel ($)</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" {...field} disabled={busy} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy} data-testid="event-submit">
                {busy ? 'Saving…' : editing ? 'Save changes' : 'Register event'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
